/**
 * Strips all non-digit characters from a string.
 * @param {string} value
 * @returns {string}
 */
export function stripNonDigits(value) {
  return value.replace(/\D/g, "");
}

/**
 * Attaches input, keydown, and paste event listeners to a single input element
 * to enforce formatter masking.
 * @param {HTMLInputElement} input
 * @returns {Function} cleanup function that removes all listeners
 */
export function applyMask(input, formatter, digitLimit) {
  function handleInput() {
    const digits = stripNonDigits(input.value);
    const masked = formatter(digits);
    if (input.value !== masked) {
      input.value = masked;
    }
  }

  function handleKeydown(event) {
    const { key } = event;

    // Allow navigation, deletion, tab, etc.
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
    if (allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return;
    }

    // Block non-digit characters
    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return;
    }

    // Block input beyond max length (digitLimit)
    const digits = stripNonDigits(input.value);
    if (digits.length >= digitLimit) {
      event.preventDefault();
    }
  }

  function handlePaste(event) {
    event.preventDefault();
    const pastedText = (event.clipboardData || window.clipboardData).getData("text");
    const digits = stripNonDigits(pastedText);
    input.value = formatter(digits);
  }

  input.addEventListener("input", handleInput);
  input.addEventListener("keydown", handleKeydown);
  input.addEventListener("paste", handlePaste);

  return function cleanup() {
    input.removeEventListener("input", handleInput);
    input.removeEventListener("keydown", handleKeydown);
    input.removeEventListener("paste", handlePaste);
  };
}

export function initializeMasks(container, selector, formatter, digitLimit) {
  if (!container) {
    return () => {};
  }

  const inputs = container.querySelectorAll(selector);
  const cleanups = Array.from(inputs).map((input) => applyMask(input, formatter, digitLimit));

  return function cleanup() {
    cleanups.forEach((fn) => fn());
  };
}
